// Define the structure for atomic comparison operators ($eq, $neq, $gt, $lt)
interface AtomicCondition {
    propertyName: string;
    propertyValue: any;
}

// Define specific interfaces for each atomic operator, including a 'kind' property
// or simply allowing the operator name itself to act as the discriminator.
// For simplicity, we'll let the property key ($eq, $neq etc.) be the discriminator.
export interface EqRequirement {
    $eq: AtomicCondition;
}

export interface NeqRequirement {
    $neq: AtomicCondition;
}

export interface NinRequirement {
    $nin: AtomicCondition;
}

export interface InRequirement {
    $in: AtomicCondition;
}

export interface GtRequirement {
    $gt: AtomicCondition;
}

export interface LtRequirement {
    $lt: AtomicCondition;
}

// Define interfaces for logical operators ($and, $or)
// Their values are arrays of other Requirement objects, allowing nesting.
export interface AndRequirement {
    $and: Requirement[];
}

export interface OrRequirement {
    $or: Requirement[];
}

export interface NotRequirement {
    $not: Requirement;
}

// A Requirement can be any one of the above types
export type Requirement =
    | EqRequirement
    | NeqRequirement
    | NinRequirement
    | InRequirement
    | GtRequirement
    | LtRequirement
    | AndRequirement
    | OrRequirement
    | NotRequirement;

/**
 * Evaluates a single requirement object against the given ViewResult.
 * This function is recursive to handle nested $and/$or conditions.
 * @param requirement The single requirement object to evaluate.
 * @param results The ViewResult object containing property values.
 * @returns true if the requirement is met, false otherwise.
 */
function evaluateSingleRequirement(requirement: Requirement, results: ViewResult, shouldIgnore: (propertyName: string) => boolean): boolean {
    if ('$eq' in requirement) {
        const { propertyName, propertyValue } = requirement.$eq;
        return results[propertyName] == propertyValue && !shouldIgnore(propertyName); // Use == for loose equality as in your original example
    } else if ('$neq' in requirement) {
        const { propertyName, propertyValue } = requirement.$neq;
        return results[propertyName] != propertyValue && !shouldIgnore(propertyName); // Use != for loose inequality
    } else if ('$nin' in requirement) {
        const { propertyName, propertyValue } = requirement.$nin;
        return (!Array.isArray(propertyValue) || !propertyValue.includes(results[propertyName])) && !shouldIgnore(propertyName); // Use != for loose inequality
    } else if ('$in' in requirement) {
        const { propertyName, propertyValue } = requirement.$in;
        return (Array.isArray(propertyValue) && propertyValue.includes(results[propertyName])) && !shouldIgnore(propertyName); // Use != for loose inequality
    } else if ('$gt' in requirement) {
        const { propertyName, propertyValue } = requirement.$gt;
        return results[propertyName] > propertyValue && !shouldIgnore(propertyName);
    } else if ('$lt' in requirement) {
        const { propertyName, propertyValue } = requirement.$lt;
        return results[propertyName] < propertyValue && !shouldIgnore(propertyName);
    } else if ('$and' in requirement) {
        // For $and, all sub-requirements must be true
        return requirement.$and.every(subReq => evaluateSingleRequirement(subReq, results, shouldIgnore));
    } else if ('$or' in requirement) {
        // For $or, at least one sub-requirement must be true
        return requirement.$or.some(subReq => evaluateSingleRequirement(subReq, results, shouldIgnore));
    } else if ('$not' in requirement) {
        // For $not, the sub-requirement must be false
        return !evaluateSingleRequirement(requirement.$not, results, shouldIgnore);
    }

    // If an unknown requirement type is encountered, it's safer to return false
    // or throw an error depending on desired strictness.
    console.warn("Unknown requirement type encountered:", requirement);
    return false;
}

/**
 * Determines if an item is hidden based on its requirements and current data.
 * @param hiddenRequirements An array of Requirement objects.
 * @param results The ViewResult object containing the data to evaluate against.
 * @returns true if the item should be hidden, false otherwise.
 */
export function isHidden(hiddenRequirements: Requirement[] | undefined, results: ViewResult, shouldIgnore: (propertyName: string) => boolean = () => false): boolean {
    // If there are no requirements, the item is not hidden by default.
    if (!hiddenRequirements || hiddenRequirements.length === 0) {
        return false;
    }

    // If the 'hidden' array contains multiple top-level requirements,
    // they are implicitly combined with an AND operation.
    // This means all top-level requirements must evaluate to true for the item to be hidden.
    return hiddenRequirements.every(req => evaluateSingleRequirement(req, results, shouldIgnore));
}